import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useRef } from "react";

import CBox from "../../../basecomp/newComponents/CBox";
import CText from "../../../basecomp/newComponents/CText";
import CInput from "../../../basecomp/newComponents/CInput";
import CButton from "../../../basecomp/newComponents/CButton";
import CGrid from "../../../basecomp/newComponents/CGrid";
import CDropdown from "../../../basecomp/newComponents/CDropdown";
import CModal from "../../../basecomp/newComponents/CModal";
import CTooltip from '../../../basecomp/newComponents/CTooltip';
import DataDetailVM from "../viewmodel/DataDetailVM";
import { updateData } from "../model/DataProcessingModel";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import CircularProgress from "@mui/material/CircularProgress";

// Icons
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { Paper, Box, Typography, LinearProgress } from "@mui/material";

// Enhanced Material Enrichment Service with configurable API key
class MaterialEnrichmentService {
  constructor(apiKey = null) {
    this.apiKey = apiKey || process.env.REACT_APP_GEMINI_API_KEY || '';
    this.baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent';
    
    if (!this.apiKey) {
      throw new Error('API key is required. Please set REACT_APP_GEMINI_API_KEY environment variable or pass it to the constructor.');
    }
  }

  setApiKey(apiKey) {
    this.apiKey = apiKey;
  }

  getApiUrl() {
    return `${this.baseUrl}?key=${this.apiKey}`;
  }

  async enrichMaterialData({ materialDescription, partNumber, manufacturer, attributes, commodity }) {
    const requestBody = {
      generationConfig: {
        temperature: 0.0
      },
      system_instruction: {
        parts: [
          {
            text: `You are a technical data enrichment assistant specializing in industrial components and materials. Your task is to extract and provide technical specifications based on the available product information.

**Input Data:**
You will receive:
- Material Description (may contain embedded specifications)
- Part Number (if available)
- Manufacturer (if available)
- List of required attributes to find

**Core Instructions:**

1. **Extract Direct Specifications First:**
   - Carefully analyze the Material Description for any embedded technical values
   - Examples: "MOTOR 5HP 220V" → Power=5HP, Voltage=220V
   - Examples: "BATTERY 12V 100AH" → Voltage=12V, Capacity=100AH
   - Examples: "CONTACTOR 50A 3PHASE" → Current=50A, Type=3Phase

2. **Find Missing Specifications:**
   - Use the manufacturer and part number to identify specific product specifications
   - Use the material description to determine typical specifications for that component type
   - Focus on the most common and reliable specifications for the component category

3. **Attribute Handling:**
   - Provide values for ALL requested attributes when possible
   - Use "N/A" only when genuinely no information can be determined
   - Be consistent with units and formatting
   - Provide ranges when exact values aren't available (e.g., "10-15W")
   - Use simple attribute names like: Voltage, Current, Power, Frequency, etc.

4. **Commodity Classification:**
   - Identify the main component category (e.g., Motor, Battery, Contactor, Relay, Sensor, etc.)
   - Use standard industrial terminology

**Output Format:**
Return data as a markdown table with "Parameter" and "Value" columns:

| Parameter | Value |
|---|---|
| [Attribute1] | [Value1] |
| [Attribute2] | [Value2] |
| ... | ... |
| Commodity |  |

**Key Requirements:**
- Always respond with the table format, even if some values are N/A
- Be consistent with your responses
- Focus on accuracy over completeness
- Use standard industrial units and terminology
- Keep responses concise and factual
- DO NOT include any reference links or citations

**Example Response:**
| Parameter | Value |
|---|---|
| Voltage | 24V |
| Current | 10A |
| Power | 240W |
| Type | DC Motor |
| Commodity | Motor |`
          }
        ]
      },
      contents: [
        {
          role: "user",
          parts: [
            {
              text: this.buildPromptText({ materialDescription, partNumber, manufacturer, attributes, commodity })
            }
          ]
        }
      ]
    };

    console.log('🚀 Enrichment API Request:', {
      materialDescription,
      partNumber,
      manufacturer,
      commodity,
      attributes,
      apiUrl: this.getApiUrl().replace(/key=[^&]+/, 'key=***'),
      requestBody: JSON.stringify(requestBody, null, 2)
    });

    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Request timed out after 60 seconds')), 60000)
    );

    const fetchPromise = fetch(this.getApiUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    try {
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      console.log('📊 Response Status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Request Failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        
        let friendlyMessage = 'Unable to enrich material data at this time.';
        if (response.status === 429) {
          friendlyMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (response.status === 401) {
          friendlyMessage = 'Authentication failed. Please contact your administrator.';
        } else if (response.status === 403) {
          friendlyMessage = 'Access denied. Please contact your administrator.';
        } else if (response.status >= 500) {
          friendlyMessage = 'Service temporarily unavailable. Please try again later.';
        }
        
        throw new Error(friendlyMessage);
      }

      const data = await response.json();
      
      console.log('📥 Raw API Response:', JSON.stringify(data, null, 2));
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        const generatedText = data.candidates[0].content.parts[0].text;
        console.log('📝 Generated Text:', generatedText);
        return data;
      } else {
        console.error('❌ No content in response:', data);
        throw new Error('No specifications found. Please try with different material information.');
      }

    } catch (error) {
      if (error.message.includes('timed out after')) {
        throw new Error('The enrichment process took too long. Please try again.');
      }
      console.error('💥 Fetch Error:', error);
      throw error;
    }
  }

  buildPromptText({ materialDescription, partNumber, manufacturer, attributes, commodity }) {
    let prompt = "";
    
    if (materialDescription && materialDescription.trim()) {
      prompt += `Material Description: ${materialDescription}\n`;
    } else {
      prompt += `Material Description: \n`;
    }
    
    if (partNumber && partNumber.trim()) {
      prompt += `Part Number: ${partNumber}\n`;
    } else {
      prompt += `Part Number: \n`;
    }
    
    if (manufacturer && manufacturer.trim()) {
      prompt += `Manufacturer: ${manufacturer}\n`;
    } else {
      prompt += `Manufacturer: \n`;
    }
    
    prompt += `Attributes Required: ${attributes.join(', ')}`;
    
    console.log('🔤 Built Prompt Text:', prompt);
    
    return prompt;
  }

  parseMarkdownTable(markdownText, frontendAttributeNames = []) {
    console.log('🔍 Parsing Markdown Table:', markdownText);
    console.log('🎯 Frontend Attribute Names:', frontendAttributeNames);
    
    if (!markdownText || markdownText.trim() === '') {
      console.warn('⚠️ Empty markdown text received');
      return { attributes: {}, commodityName: '', searchQuery: '' };
    }
    
    const lines = markdownText.split('\n').filter(line => line.trim());
    const attributes = {};
    let commodityName = '';
    let searchQuery = '';

    if (lines.length < 3) {
      console.warn('⚠️ Invalid table structure - too few lines');
      return { attributes: {}, commodityName: '', searchQuery: '' };
    }

    const attributeMapping = this.createAttributeMapping(frontendAttributeNames);
    console.log('🗺️ Attribute Mapping:', attributeMapping);

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (line.includes('Parameter') || line.includes('---') || line.includes('===')) {
        continue;
      }
      
      if (line.startsWith('|') && line.endsWith('|')) {
        const cells = line.split('|').map(cell => cell.trim()).filter((cell, index) => {
          return index === 0 ? false : cell !== '';
        });
        
        if (cells.length >= 2) {
          const parameter = cells[0].trim();
          const value = cells[1].trim();
          
          console.log(`📋 Found parameter: "${parameter}" = "${value}"`);
          
          if (parameter.startsWith('Reference Link')) {
            console.log(`🚫 Skipping reference link: ${parameter}`);
            continue;
          }
          
          if (parameter === 'Search Query') {
            searchQuery = value === 'N/A' ? '' : value;
            console.log(`🔍 Search Query found: ${searchQuery}`);
          } else if (parameter === 'Commodity') {
            commodityName = value === 'N/A' ? '' : value;
            console.log(`🏷️ Commodity found: ${commodityName}`);
          } else if (value && value !== 'N/A' && value.trim() !== '') {
            const matchedAttribute = this.findBestAttributeMatch(parameter, attributeMapping);
            
            if (matchedAttribute) {
              attributes[matchedAttribute] = value;
              console.log(`✅ Mapped "${parameter}" to "${matchedAttribute}" = ${value}`);
            } else {
              attributes[parameter] = value;
              console.log(`⚠️ No mapping found, using original: ${parameter} = ${value}`);
            }
          }
        }
      }
    }

    const result = { attributes, commodityName, searchQuery };
    console.log('✅ Final Parsed Result:', result);
    
    return result;
  }

  createAttributeMapping(frontendAttributeNames) {
    const mapping = {};
    
    frontendAttributeNames.forEach(frontendName => {
      const baseName = frontendName.replace(/\s*\([^)]*\)\s*/g, '').trim().toLowerCase();
      
      const variations = [
        baseName,
        baseName.replace(/\s+/g, ''),
        baseName.replace(/\s+/g, '_'),
        baseName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(''),
        baseName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      ];
      
      variations.forEach(variation => {
        mapping[variation.toLowerCase()] = frontendName;
      });
    });
    
    const commonMappings = {
      'voltage': this.findAttributeByKeyword(frontendAttributeNames, ['voltage', 'volt']),
      'current': this.findAttributeByKeyword(frontendAttributeNames, ['current', 'amp']),
      'power': this.findAttributeByKeyword(frontendAttributeNames, ['power', 'watt']),
      'frequency': this.findAttributeByKeyword(frontendAttributeNames, ['frequency', 'freq', 'hz']),
      'capacity': this.findAttributeByKeyword(frontendAttributeNames, ['capacity', 'cap']),
      'temperature': this.findAttributeByKeyword(frontendAttributeNames, ['temperature', 'temp']),
      'pressure': this.findAttributeByKeyword(frontendAttributeNames, ['pressure', 'press']),
      'speed': this.findAttributeByKeyword(frontendAttributeNames, ['speed', 'rpm']),
      'torque': this.findAttributeByKeyword(frontendAttributeNames, ['torque']),
      'diameter': this.findAttributeByKeyword(frontendAttributeNames, ['diameter', 'dia']),
      'length': this.findAttributeByKeyword(frontendAttributeNames, ['length', 'len']),
      'width': this.findAttributeByKeyword(frontendAttributeNames, ['width']),
      'height': this.findAttributeByKeyword(frontendAttributeNames, ['height']),
      'weight': this.findAttributeByKeyword(frontendAttributeNames, ['weight']),
      'material': this.findAttributeByKeyword(frontendAttributeNames, ['material', 'mat']),
      'type': this.findAttributeByKeyword(frontendAttributeNames, ['type']),
      'model': this.findAttributeByKeyword(frontendAttributeNames, ['model']),
      'size': this.findAttributeByKeyword(frontendAttributeNames, ['size'])
    };
    
    Object.keys(commonMappings).forEach(key => {
      if (commonMappings[key]) {
        mapping[key] = commonMappings[key];
      }
    });
    
    return mapping;
  }

  findAttributeByKeyword(frontendAttributeNames, keywords) {
    for (const keyword of keywords) {
      const found = frontendAttributeNames.find(attr => 
        attr.toLowerCase().includes(keyword.toLowerCase())
      );
      if (found) return found;
    }
    return null;
  }

  findBestAttributeMatch(aiParameter, attributeMapping) {
    const aiParamLower = aiParameter.toLowerCase().trim();
    
    if (attributeMapping[aiParamLower]) {
      return attributeMapping[aiParamLower];
    }
    
    const mappingEntries = Object.entries(attributeMapping);
    
    for (const [mappingKey, frontendName] of mappingEntries) {
      if (aiParamLower.includes(mappingKey) || mappingKey.includes(aiParamLower)) {
        return frontendName;
      }
    }
    
    return null;
  }

  isValidUrl(string) {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  }
}

// Confirmation modal component for cancellation
const ConfirmCancelModal = ({ open, onClose, onConfirm, onCancel }) => {
  return (
    <CModal
      open={open}
      handleClose={onClose}
      title="Save Temporary Data"
      actions={[
        {
          label: "No",
          onClick: onCancel,
          color: "error",
          variant: "outlined",
        },
        {
          label: "Yes",
          onClick: onConfirm,
          color: "primary",
          variant: "contained",
        }
      ]}
      maxWidth="sm"
      headerBgColor="#002A5B"
    >
      <CBox sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <InfoIcon sx={{ color: 'red', fontSize: 32 }} />
        <CBox>
          <CText variant="h6" sx={{ mb: 1, fontWeight: 500 }}>Do you want to save your data temporarily?</CText>
          <CText>
            If you select "Yes", your changes will be saved temporarily and will be available when you edit this row again.
            If you select "No", all unsaved changes will be lost.
          </CText>
        </CBox>
      </CBox>
    </CModal>
  );
};

// Embedded CLoader component
const CLoader = ({
  progress = 0,
  theme,
  sx = {},
}) => {
  const [displayProgress, setDisplayProgress] = useState(progress);
  const prevProgressRef = useRef(progress);

  useEffect(() => {
    if (progress > prevProgressRef.current) {
      setDisplayProgress(progress);
      prevProgressRef.current = progress;
    }
  }, [progress]);

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backdropFilter: "blur(3px)"
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 3,
            maxWidth: "600px",
            width: "90%",
            bgcolor: theme?.palette?.background?.paper || "#fff",
            borderColor: theme?.palette?.primary?.light || "#ccc",
            borderWidth: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            ...sx,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              We are fetching the data
            </Typography>
          </Box>

          <Box sx={{ width: "100%", mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={displayProgress}
              color="secondary"
              sx={{
                height: 10,
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  transition: 'transform 0.3s ease-out'
                }
              }}
            />
          </Box>
        </Paper>
      </Box>
    </>
  );
};

const EditableModal = ({
  modalOpen,
  handleCancelClick,
  handleSaveClick,
  editData,
  handleFieldChange,
  rowId,
  setModalOpen
}) => {
  const {
    standards,
    commodities,
    attributeValues,
    localEditData,
    initializeFormData,
    handleLocalFieldChange,
    handleAttributeChange,
    handleCommentChange,
    getValue,
    getAttributeFieldNames,
    openMaterialSearch,
    openManufacturerSearch,
    handleSaveChanges
  } = DataDetailVM();

  const { handleToastOpen } = useToast();

  const [searchParams] = useSearchParams();
  const batch_id = searchParams.get('batchId') || 'default';
  const customer = searchParams.get('customer') || '';
  const project = searchParams.get('project') || '';

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [generatedLongDesc, setGeneratedLongDesc] = useState('');
  const [changedFields, setChangedFields] = useState(new Set());

  // Material Enrichment States
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichmentStatus, setEnrichmentStatus] = useState('');
  const [enrichmentMessage, setEnrichmentMessage] = useState('');
  const [citationUrl, setCitationUrl] = useState('');
  const [enrichmentAttempted, setEnrichmentAttempted] = useState(false);

  const [enrichmentProgress, setEnrichmentProgress] = useState(0);
  const [enrichmentStats, setEnrichmentStats] = useState({
    current: 0,
    total: 3,
    callsCompleted: 0,
    successfulCalls: 0
  });

  const [pendingEnrichmentData, setPendingEnrichmentData] = useState(null);
  const [showApprovalButtons, setShowApprovalButtons] = useState(false);
  const [enrichmentApproved, setEnrichmentApproved] = useState(false);
  const [referenceLinks, setReferenceLinks] = useState([]);
  const [enrichmentService, setEnrichmentService] = useState(null);

  const findStandardInDropdown = (searchName) => {
    if (!searchName || !standards || standards.length === 0) return null;
    
    const searchLower = searchName.toLowerCase().trim();
    console.log(`🔍 Searching for "${searchName}" in standards dropdown`);
    
    return standards.find(std => {
      if (!std.name) return false;
      
      const stdNameLower = std.name.toLowerCase().trim();
      
      if (stdNameLower === searchLower) {
        console.log(`✅ Exact match found: ${std.name}`);
        return true;
      }
      
      const singularSearch = searchLower.endsWith('s') ? searchLower.slice(0, -1) : searchLower + 's';
      const singularStd = stdNameLower.endsWith('s') ? stdNameLower.slice(0, -1) : stdNameLower + 's';
      
      if (stdNameLower === singularSearch || singularStd === searchLower) {
        console.log(`✅ Singular/Plural match found: ${std.name} matches ${searchName}`);
        return true;
      }
      
      if (stdNameLower.includes(searchLower) || searchLower.includes(stdNameLower)) {
        if (searchLower.length >= 3 && stdNameLower.length >= 3) {
          console.log(`✅ Partial match found: ${std.name} matches ${searchName}`);
          return true;
        }
      }
      
      return false;
    });
  };

  const attributeFieldNames = getAttributeFieldNames();

  useEffect(() => {
    const initService = () => {
      try {
        const service = new MaterialEnrichmentService();
        setEnrichmentService(service);
        console.log('✅ Enrichment service initialized');
      } catch (error) {
        console.error('❌ Failed to initialize enrichment service:', error.message);
        setEnrichmentStatus('error');
        setEnrichmentMessage('Configuration error: ' + error.message);
      }
    };

    if (modalOpen && !enrichmentService) {
      initService();
    }
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen && editData) {
      console.log('Modal opened, clearing any previous enrichment session data');
      
      const sessionKey = `${batch_id}_${rowId}`;
      try {
        const storedData = sessionStorage.getItem(sessionKey);
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          
          const cleanedData = {};
          Object.keys(parsedData).forEach(key => {
            // Don't clear Special Features - keep user's manual input
            if (!key.includes('Long Description') && !key.startsWith('ai_enriched_')) {
              cleanedData[key] = parsedData[key];
            }
          });
          
          sessionStorage.setItem(sessionKey, JSON.stringify(cleanedData));
        }
      } catch (error) {
        console.error('Error cleaning session data:', error);
      }
      
      initializeFormData(editData);
      
      setGeneratedLongDesc('');
      setChangedFields(new Set());
      setEnrichmentStatus('');
      setEnrichmentMessage('');
      setCitationUrl('');
      setEnrichmentAttempted(false);
      setPendingEnrichmentData(null);
      setShowApprovalButtons(false);
      setEnrichmentApproved(false);
      setReferenceLinks([]);
      setEnrichmentProgress(0);
      setEnrichmentStats({
        current: 0,
        total: 3,
        callsCompleted: 0,
        successfulCalls: 0
      });
    }
  }, [modalOpen, editData?.id, rowId]);

  // FIX 3: Enhanced Material Enrichment - Uses ALL descriptions and manufacturers
  const handleMaterialEnrichment = async () => {
    if (!enrichmentService) {
      setEnrichmentStatus('error');
      setEnrichmentMessage('Service configuration error. Please contact support.');
      return;
    }

    // FIX 3: Gather ALL material descriptions (1, 2, 3)
    const materialDesc1 = getSessionValue('Material Description', localEditData?.material_description || '');
    const materialDesc2 = getSessionValue('Material Description 2', localEditData?.material_description_2 || '');
    const materialDesc3 = getSessionValue('Material Description 3', localEditData?.material_description_3 || '');
    
    const allMaterialDescs = [materialDesc1, materialDesc2, materialDesc3]
      .filter(desc => desc && desc.trim() !== '')
      .join(' | ');
    
    // FIX 3: Gather ALL manufacturers and part numbers (1, 2, 3)
    const manufacturer1 = getSessionValue('Manufacturer 1', localEditData?.manufacturer_name_1 || '');
    const partNumber1 = getSessionValue('Part Number 1', localEditData?.manufacturer_part_number_1 || '');
    const manufacturer2 = getSessionValue('Manufacturer 2', localEditData?.manufacturer_name_2 || '');
    const partNumber2 = getSessionValue('Part Number 2', localEditData?.manufacturer_part_number_2 || '');
    const manufacturer3 = getSessionValue('Manufacturer 3', localEditData?.manufacturer_name_3 || '');
    const partNumber3 = getSessionValue('Part Number 3', localEditData?.manufacturer_part_number_3 || '');
    
    const allManufacturers = [manufacturer1, manufacturer2, manufacturer3]
      .filter(mfr => mfr && mfr.trim() !== '')
      .join(' | ');
    
    const allPartNumbers = [partNumber1, partNumber2, partNumber3]
      .filter(pn => pn && pn.trim() !== '')
      .join(' | ');
    
    const commodity = getSessionValue('Commodity', getValue('commodity', editData?.['Commodity'] || ''));
    
    console.log('🎯 Starting Material Enrichment with ALL available data:', {
      materialDescs: allMaterialDescs ? 'Present' : 'Missing',
      manufacturers: allManufacturers ? 'Present' : 'Missing', 
      partNumbers: allPartNumbers ? 'Present' : 'Missing',
      commodity: commodity ? 'Present' : 'Missing'
    });
    
    const hasMinimumInfo = allMaterialDescs.trim() || allManufacturers.trim() || allPartNumbers.trim();
    
    if (!hasMinimumInfo) {
      setEnrichmentStatus('warning');
      setEnrichmentMessage('Please provide at least one of the following: Material Description, Manufacturer, or Part Number to get technical specifications.');
      return;
    }

    if (attributeFieldNames.length === 0) {
      setEnrichmentStatus('warning');
      setEnrichmentMessage('No technical attributes are available for enrichment. Please contact your administrator.');
      return;
    }

    setIsEnriching(true);
    setEnrichmentProgress(0);
    setEnrichmentStats({
      current: 0,
      total: 3,
      callsCompleted: 0,
      successfulCalls: 0
    });
    setEnrichmentStatus('');
    setEnrichmentMessage('');
    setEnrichmentAttempted(true);
    setPendingEnrichmentData(null);
    setShowApprovalButtons(false);
    setEnrichmentApproved(false);
    setReferenceLinks([]);

    const TOTAL_CALLS = 3;
    const CALL_DELAY = 1500;
    
    let callResults = [];
    let successfulCalls = 0;
    let totalApiCalls = 0;

    for (let callIndex = 1; callIndex <= TOTAL_CALLS; callIndex++) {
      try {
        totalApiCalls++;
        console.log(`🔄 Enrichment Call ${callIndex}/${TOTAL_CALLS}`);
        
        setEnrichmentProgress(((callIndex - 1) / TOTAL_CALLS) * 100);
        setEnrichmentStats(prev => ({
          ...prev,
          current: callIndex,
          callsCompleted: callIndex - 1
        }));
        
        setEnrichmentMessage(`Making API call ${callIndex}/${TOTAL_CALLS} to gather comprehensive data...`);

        // FIX 3: Use ALL combined data in API call
        const response = await enrichmentService.enrichMaterialData({
          materialDescription: allMaterialDescs,
          partNumber: allPartNumbers,
          manufacturer: allManufacturers,
          commodity: commodity,
          attributes: attributeFieldNames
        });

        console.log(`📥 API Response received for call ${callIndex}:`, response);

        if (response.candidates && response.candidates[0] && response.candidates[0].content) {
          const generatedText = response.candidates[0].content.parts[0].text;
          console.log(`📝 Generated text for call ${callIndex}:`, generatedText);
          
          const { attributes, commodityName, searchQuery } = enrichmentService.parseMarkdownTable(
            generatedText, 
            attributeFieldNames
          );
          
          console.log(`🎉 Call ${callIndex} parsed:`, {
            attributesFound: Object.keys(attributes).length,
            attributes: attributes,
            commodityName: commodityName,
            searchQuery: searchQuery
          });

          callResults.push({
            callIndex: callIndex,
            attributes: attributes || {},
            commodityName: commodityName || '',
            searchQuery: searchQuery || '',
            success: true
          });
          
          successfulCalls++;
          
          setEnrichmentStats(prev => ({
            ...prev,
            successfulCalls: successfulCalls,
            callsCompleted: callIndex
          }));
          
        } else {
          console.error(`❌ Invalid response structure for call ${callIndex}:`, response);
          callResults.push({
            callIndex: callIndex,
            attributes: {},
            commodityName: '',
            searchQuery: '',
            success: false,
            error: 'Invalid response format'
          });
          
          setEnrichmentStats(prev => ({
            ...prev,
            callsCompleted: callIndex
          }));
        }

      } catch (error) {
        console.error(`💥 Enrichment error on call ${callIndex}:`, error);
        callResults.push({
          callIndex: callIndex,
          attributes: {},
          commodityName: '',
          searchQuery: '',
          success: false,
          error: error.message
        });
        
        setEnrichmentStats(prev => ({
          ...prev,
          callsCompleted: callIndex
        }));
      }

      setEnrichmentProgress((callIndex / TOTAL_CALLS) * 100);

      if (callIndex < TOTAL_CALLS) {
        console.log(`⏳ Waiting ${CALL_DELAY}ms before next call...`);
        setEnrichmentMessage(`Waiting before next call... (${callIndex}/${TOTAL_CALLS} completed)`);
        await new Promise(resolve => setTimeout(resolve, CALL_DELAY));
      }
    }

    try {
      console.log('🔄 Consolidating results from all calls:', callResults);
      
      setEnrichmentMessage(`Analyzing and consolidating data from ${successfulCalls}/${TOTAL_CALLS} successful calls...`);
      setEnrichmentProgress(100);

      const consolidatedAttributes = consolidateAttributesFromCalls(callResults, attributeFieldNames);
      const consolidatedCommodityName = consolidateCommodityFromCalls(callResults);
      const consolidatedSearchQuery = consolidateSearchQueryFromCalls(callResults);

      console.log('🏁 Final consolidated results:', {
        totalAttributes: Object.keys(consolidatedAttributes).length,
        attributes: consolidatedAttributes,
        commodityName: consolidatedCommodityName,
        searchQuery: consolidatedSearchQuery,
        successfulCalls: successfulCalls,
        totalApiCalls: totalApiCalls
      });

      const hasAttributes = Object.keys(consolidatedAttributes).length > 0;
      const hasCommodityName = consolidatedCommodityName && consolidatedCommodityName.trim() !== '';
      const hasAnyData = hasAttributes || hasCommodityName;

      if (hasAnyData) {
        const pendingData = {
          attributes: consolidatedAttributes,
          commodityName: consolidatedCommodityName,
          searchQuery: consolidatedSearchQuery
        };
        setPendingEnrichmentData(pendingData);

        if (hasAttributes) {
          console.log('📋 Processing consolidated attributes...');
          
          Object.keys(consolidatedAttributes).forEach(fieldName => {
            const value = consolidatedAttributes[fieldName];
            console.log(`✅ Populating ${fieldName} = ${value}`);
            
            const sessionKey = `${batch_id}_${rowId}`;
            const fullFieldName = `attr_${fieldName}`;
            let rowData = {};
            try {
              const storedData = sessionStorage.getItem(sessionKey);
              if (storedData) {
                rowData = JSON.parse(storedData);
              }
            } catch (error) {
              console.error('Error parsing session data:', error);
            }
            
            rowData[fullFieldName] = value;
            sessionStorage.setItem(sessionKey, JSON.stringify(rowData));
            
            handleAttributeChange(fieldName, value);
            
            const currentAttribute = localEditData.attribute || {};
            const updatedAttribute = {
              ...currentAttribute,
              [fieldName]: value
            };
            handleFieldChange('attribute', updatedAttribute);
          });
        }

        if (hasCommodityName) {
          console.log('🏷️ Processing consolidated commodity name:', consolidatedCommodityName);
          
          const matchedStandard = findStandardInDropdown(consolidatedCommodityName);
          
          const sessionKey = `${batch_id}_${rowId}`;
          let rowData = {};
          try {
            const storedData = sessionStorage.getItem(sessionKey);
            if (storedData) {
              rowData = JSON.parse(storedData);
            }
          } catch (error) {
            console.error('Error parsing session data:', error);
          }
          
          if (matchedStandard) {
            console.log(`✅ Commodity found in Standard Name dropdown: ${matchedStandard.name}`);
            rowData['Standard Name'] = matchedStandard.name;
            sessionStorage.setItem(sessionKey, JSON.stringify(rowData));
            handleLocalFieldChange('Standard Name', matchedStandard.name);
            handleFieldChange('Standard Name', matchedStandard.name);
          } else {
            console.log(`✅ Commodity added to Standard Name 2: ${consolidatedCommodityName}`);
            rowData['Standard Name 2'] = consolidatedCommodityName;
            sessionStorage.setItem(sessionKey, JSON.stringify(rowData));
            handleLocalFieldChange('Standard Name 2', consolidatedCommodityName);
            handleFieldChange('Standard Name 2', consolidatedCommodityName);
          }
        }
        
        // FIX 2 & 4: Generate and save long description immediately
        setTimeout(() => {
          const newDesc = generateLongDescription();
          if (newDesc && newDesc.trim()) {
            setGeneratedLongDesc(newDesc);
            
            const sessionKey = `${batch_id}_${rowId}`;
            let rowData = {};
            try {
              const storedData = sessionStorage.getItem(sessionKey);
              if (storedData) {
                rowData = JSON.parse(storedData);
              }
            } catch (error) {
              console.error('Error parsing session data for long description:', error);
            }
            
            rowData['Long Description'] = newDesc;
            sessionStorage.setItem(sessionKey, JSON.stringify(rowData));
            handleFieldChange('long_description', newDesc);
          }
        }, 100);
        
        let message = '';
        const attrCount = Object.keys(consolidatedAttributes).length;
        const callsText = successfulCalls > 1 ? `` : '';
        
        if (hasAttributes && hasCommodityName) {
          message = `Found ${attrCount} technical specification${attrCount !== 1 ? 's' : ''} and component classification${callsText}. Please review and approve if correct.`;
        } else if (hasAttributes) {
          message = `Found ${attrCount} technical specification${attrCount !== 1 ? 's' : ''}${callsText}. Please review and approve if correct.`;
        } else if (hasCommodityName) {
          message = `Found component classification${callsText}. Please review and approve if correct.`;
        }
        
        console.log('✅ Setting success status and showing approval buttons');
        setEnrichmentStatus('success');
        setEnrichmentMessage(message);
        setShowApprovalButtons(true);
        
      } else {
        console.log('❌ No meaningful data found after all calls');
        
        let errorMessage;
        if (successfulCalls === 0) {
          errorMessage = 'All 3 API calls failed. Please check your internet connection and try again.';
        } else if (successfulCalls < 3) {
          errorMessage = `Only ${successfulCalls} out of 3 API calls succeeded, but no meaningful technical specifications were found. The AI may not have sufficient knowledge about this material.`;
        } else {
          errorMessage = 'All 3 API calls completed successfully, but no meaningful technical specifications were found. The AI may not have sufficient knowledge about this material.';
        }
          
        setEnrichmentStatus('error');
        setEnrichmentMessage(errorMessage);
        setShowApprovalButtons(false);
      }

    } catch (processingError) {
      console.error('💥 Error processing consolidated results:', processingError);
      setEnrichmentStatus('error');
      setEnrichmentMessage('Error processing enrichment results. Please try again.');
    } finally {
      setIsEnriching(false);
      setEnrichmentProgress(100);
      console.log(`🏁 Enrichment process completed. Made ${totalApiCalls} API calls with ${successfulCalls} successful attempts.`);
    }
  };

  const consolidateAttributesFromCalls = (callResults, attributeFieldNames) => {
    console.log('🔄 Starting attribute consolidation...');
    
    const consolidated = {};
    const attributeValueMap = {};
    
    attributeFieldNames.forEach(fieldName => {
      attributeValueMap[fieldName] = [];
    });
    
    callResults.forEach((result, index) => {
      if (result.success && result.attributes) {
        Object.keys(result.attributes).forEach(attrName => {
          const value = result.attributes[attrName];
          if (value && value.trim() !== '' && value !== 'N/A') {
            const matchedFieldName = findMatchingFieldName(attrName, attributeFieldNames);
            if (matchedFieldName) {
              if (!attributeValueMap[matchedFieldName]) {
                attributeValueMap[matchedFieldName] = [];
              }
              attributeValueMap[matchedFieldName].push({
                value: value.trim(),
                callIndex: index + 1,
                confidence: 1
              });
              console.log(`📝 Call ${index + 1}: Found ${matchedFieldName} = ${value}`);
            }
          }
        });
      }
    });
    
    Object.keys(attributeValueMap).forEach(fieldName => {
      const values = attributeValueMap[fieldName];
      
      if (values.length === 0) {
        return;
      } else if (values.length === 1) {
        consolidated[fieldName] = values[0].value;
        console.log(`✅ ${fieldName}: Single value = ${values[0].value}`);
      } else {
        const consolidatedValue = consolidateMultipleValues(values, fieldName);
        if (consolidatedValue) {
          consolidated[fieldName] = consolidatedValue;
          console.log(`✅ ${fieldName}: Consolidated value = ${consolidatedValue}`);
        }
      }
    });
    
    console.log('🎯 Attribute consolidation complete:', consolidated);
    return consolidated;
  };

  const consolidateMultipleValues = (values, fieldName) => {
    console.log(`🔍 Consolidating multiple values for ${fieldName}:`, values.map(v => v.value));
    
    const uniqueValues = [];
    const seenValues = new Set();
    
    values.forEach(valueObj => {
      const normalizedValue = valueObj.value.toLowerCase().trim();
      if (!seenValues.has(normalizedValue)) {
        seenValues.add(normalizedValue);
        uniqueValues.push(valueObj);
      }
    });
    
    if (uniqueValues.length === 1) {
      return uniqueValues[0].value;
    }
    
    console.log(`🤔 Multiple unique values for ${fieldName}:`, uniqueValues.map(v => v.value));
    
    if (isNumericalAttribute(fieldName)) {
      return consolidateNumericalValues(uniqueValues);
    }
    
    return consolidateTextValues(uniqueValues);
  };

  const isNumericalAttribute = (fieldName) => {
    const numericalKeywords = ['voltage', 'current', 'power', 'frequency', 'temperature', 'pressure', 'speed', 'capacity', 'diameter', 'length', 'width', 'height', 'weight', 'size'];
    const fieldLower = fieldName.toLowerCase();
    return numericalKeywords.some(keyword => fieldLower.includes(keyword));
  };

  const consolidateNumericalValues = (values) => {
    console.log(`🔢 Using first numerical value: ${values[0].value}`);
    return values[0].value;
  };

  const consolidateTextValues = (values) => {
    console.log(`📝 Using first text value: ${values[0].value}`);
    return values[0].value;
  };

  const findMatchingFieldName = (aiAttributeName, fieldNames) => {
    const aiAttrLower = aiAttributeName.toLowerCase().trim();
    
    const exactMatch = fieldNames.find(name => name.toLowerCase() === aiAttrLower);
    if (exactMatch) return exactMatch;
    
    const partialMatch = fieldNames.find(name => {
      const nameLower = name.toLowerCase();
      return nameLower.includes(aiAttrLower) || aiAttrLower.includes(nameLower);
    });
    if (partialMatch) return partialMatch;
    
    if (enrichmentService) {
      const mapping = enrichmentService.createAttributeMapping(fieldNames);
      return enrichmentService.findBestAttributeMatch(aiAttributeName, mapping);
    }
    
    return null;
  };

  const consolidateCommodityFromCalls = (callResults) => {
    for (const result of callResults) {
      if (result.success && result.commodityName && result.commodityName.trim() !== '' && result.commodityName !== 'N/A') {
        console.log(`🏷️ Using commodity from call ${result.callIndex}: ${result.commodityName}`);
        return result.commodityName.trim();
      }
    }
    return '';
  };

  const consolidateSearchQueryFromCalls = (callResults) => {
    for (const result of callResults) {
      if (result.success && result.searchQuery && result.searchQuery.trim() !== '' && result.searchQuery !== 'N/A') {
        console.log(`🔍 Using search query from call ${result.callIndex}: ${result.searchQuery}`);
        return result.searchQuery.trim();
      }
    }
    return '';
  };

  const handleApproveEnrichment = () => {
    if (!pendingEnrichmentData) return;

    console.log('✅ Approving enrichment data');
    
    setEnrichmentApproved(true);
    setShowApprovalButtons(false);
    setEnrichmentStatus('success');
    setEnrichmentMessage('Enrichment data approved. The enriched fields are now protected to preserve the AI-generated specifications.');
  };

  const handleRejectEnrichment = () => {
    if (!pendingEnrichmentData) return;

    console.log('❌ Rejecting enrichment data');
    
    const { attributes, commodityName } = pendingEnrichmentData;

    attributeFieldNames.forEach(fieldName => {
      if (attributes[fieldName]) {
        console.log(`🗑️ Clearing ${fieldName}`);
        handleAttributeChangeWrapper(fieldName, '');
      }
    });

    if (commodityName && commodityName !== 'N/A') {
      const foundStandard = findStandardInDropdown(commodityName);
      
      if (foundStandard) {
        console.log('🗑️ Clearing Standard Name');
        handleFieldChangeWrapper('Standard Name', '');
      } else {
        console.log('🗑️ Clearing Standard Name 2');
        handleFieldChangeWrapper('Standard Name 2', '');
      }
    }

    setPendingEnrichmentData(null);
    setShowApprovalButtons(false);
    setEnrichmentApproved(false);
    setEnrichmentStatus('warning');
    setEnrichmentMessage('Enrichment data rejected. All populated fields have been cleared.');
  };

  const getEnrichmentStatusIcon = () => {
    switch (enrichmentStatus) {
      case 'success':
        return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  // FIX 1 & 4: Enhanced generateLongDescription WITH Special Features support
  const generateLongDescription = useCallback(() => {
    const sessionKey = `${batch_id}_${rowId}`;
    let attributeData = {};
    let standardName = '';
    let standardName2 = '';

    let sessionData = {};
    try {
      const storedData = sessionStorage.getItem(sessionKey);
      if (storedData) {
        sessionData = JSON.parse(storedData);
      }
    } catch (error) {
      console.error('Error parsing session data for long description:', error);
    }

    if (sessionData.hasOwnProperty('Standard Name')) {
      standardName = sessionData['Standard Name'] || '';
    } else {
      standardName = localEditData?.name || editData?.['Standard Name'] || editData?.name || '';
    }

    if (sessionData.hasOwnProperty('Standard Name 2')) {
      standardName2 = sessionData['Standard Name 2'] || '';
    } else {
      standardName2 = localEditData?.standard_name_2 || editData?.['Standard Name 2'] || editData?.standard_name_2 || '';
    }

    if (editData?.attribute) {
      Object.keys(editData.attribute).forEach(key => {
        if (editData.attribute[key]) {
          attributeData[key] = editData.attribute[key];
        }
      });
    }

    if (localEditData?.attribute) {
      Object.keys(localEditData.attribute).forEach(key => {
        if (localEditData.attribute[key]) {
          attributeData[key] = localEditData.attribute[key];
        }
      });
    }

    Object.keys(sessionData).forEach(key => {
      if (key.startsWith('attr_')) {
        const attrName = key.replace('attr_', '');
        if (sessionData[key] === '' || sessionData[key] === null || sessionData[key] === undefined) {
          delete attributeData[attrName];
        } else {
          attributeData[attrName] = sessionData[key];
        }
      }
    });

    const removeTextInParentheses = (text) => {
      if (!text || typeof text !== 'string') return text;
      return text.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
    };

    let desc = '';
    const hasStandardName1 = standardName && standardName.trim() !== '';
    const hasStandardName2 = standardName2 && standardName2.trim() !== '';

    if (hasStandardName1 && hasStandardName2) {
      desc += removeTextInParentheses(standardName2.trim()) + ' ';
    } else if (hasStandardName1) {
      desc += removeTextInParentheses(standardName.trim()) + ' ';
    } else if (hasStandardName2) {
      desc += removeTextInParentheses(standardName2.trim()) + ' ';
    }

    // NEW: NO excluded attributes - Special Features will now be included!
    const excludedAttributes = []; // Empty array means nothing is excluded

    const normalizeFieldName = (fieldName) => {
      return fieldName.replace(/\([^)]*\)/g, '').replace(/\s+/g, '').trim().toLowerCase();
    };

    const isExcludedField = (fieldName) => {
      const normalized = normalizeFieldName(fieldName);
      return excludedAttributes.some(excluded => {
        const normalizedExcluded = excluded.replace(/\s+/g, '').toLowerCase();
        return normalized === normalizedExcluded || normalized.includes(normalizedExcluded);
      });
    };

    // FIX 4: Process attributes IN ORDER as they appear in attributeFieldNames
    const orderedAttributes = [];
    
    attributeFieldNames.forEach(fieldName => {
      const value = attributeData[fieldName];
      if (value && value.trim() !== '' && !isExcludedField(fieldName)) {
        orderedAttributes.push({
          key: fieldName,
          value: value.trim(),
          cleanValue: removeTextInParentheses(value.trim())
        });
      }
    });

    // FIX 1: Special handling for dimensional attributes (group them together)
    const dimensionKeywords = ['diameter', 'width', 'length', 'height', 'thickness', 'depth'];
    const dimensionFields = [];
    const nonDimensionFields = [];

    orderedAttributes.forEach(attr => {
      const normalizedKey = normalizeFieldName(attr.key);
      const isDimension = dimensionKeywords.some(keyword => normalizedKey.includes(keyword));
      
      if (isDimension) {
        dimensionFields.push(attr);
      } else {
        nonDimensionFields.push(attr);
      }
    });

    // Add dimensions (grouped together with 'x' separator)
    if (dimensionFields.length > 0) {
      const dimValues = dimensionFields.map(field => field.cleanValue);
      desc += dimValues.join(' x ') + ' mm ';
    }

    // FIX 1: Add other attributes IN ORDER (including Size AND Special Features)
    nonDimensionFields.forEach(field => {
      desc += field.cleanValue + ' ';
    });

    console.log('📝 Generated Long Description:', desc.trim());
    return desc.trim();
  }, [batch_id, rowId, editData?.id, localEditData?.attribute, editData?.attribute, attributeFieldNames]);

  const isCommentsEmpty = () => {
    const comments = getSessionValue('Comments', localEditData?.comments || '');
    return !comments || comments.trim() === '';
  };

  useEffect(() => {
    const newDesc = generateLongDescription();
    
    if (newDesc !== generatedLongDesc) {
      setGeneratedLongDesc(newDesc);
    }
  }, [localEditData?.attribute, editData?.attribute]);

  const saveToSession = (fieldName, value) => {
    const sessionKey = `${batch_id}_${rowId}`;
    let rowData = {};
    try {
      const storedData = sessionStorage.getItem(sessionKey);
      if (storedData) {
        rowData = JSON.parse(storedData);
      }
    } catch (error) {
      console.error('Error parsing session data:', error);
    }

    if (rowData[fieldName] !== value) {
      rowData[fieldName] = value;
      sessionStorage.setItem(sessionKey, JSON.stringify(rowData));

      setChangedFields(prev => new Set(prev).add(fieldName));
    }
  };

  const getSessionValue = (fieldName, defaultValue = '') => {
    const sessionKey = `${batch_id}_${rowId}`;
    try {
      const storedData = sessionStorage.getItem(sessionKey);
      if (storedData) {
        const rowData = JSON.parse(storedData);
        if (rowData.hasOwnProperty(fieldName)) {
          return rowData[fieldName];
        }
      }
    } catch (error) {
      console.error('Error getting session value:', error);
    }
    return defaultValue;
  };

  // FIX 2 & 4: Enhanced attribute change wrapper with immediate long description update
  const handleAttributeChangeWrapper = (fieldName, value) => {
    const fullFieldName = `attr_${fieldName}`;

    saveToSession(fullFieldName, value);
    handleAttributeChange(fieldName, value);

    const currentAttribute = localEditData.attribute || {};
    const updatedAttribute = {
      ...currentAttribute,
      [fieldName]: value
    };

    handleFieldChange('attribute', updatedAttribute);

    // FIX 4: Regenerate long description immediately when attribute changes
    setTimeout(() => {
      const newDesc = generateLongDescription();
      if (newDesc !== generatedLongDesc) {
        setGeneratedLongDesc(newDesc);
        
        // FIX 2: Save long description immediately to session storage
        saveToSession('Long Description', newDesc);
        handleFieldChange('long_description', newDesc);
      }
    }, 0);
  };

  const handleFieldChangeWrapper = (fieldName, value) => {
    saveToSession(fieldName, value);
    handleLocalFieldChange(fieldName, value);
    handleFieldChange(fieldName, value);

    if (fieldName === 'Standard Name' || fieldName === 'Standard Name 2') {
      const newDesc = generateLongDescription();
      setGeneratedLongDesc(newDesc);
    }
  };

  const handleCommentChangeWrapper = (value) => {
    saveToSession('Comments', value);
  };

  const isFieldDisabled = (fieldName) => {
    if (!enrichmentApproved || !pendingEnrichmentData) return false;
    
    if (fieldName.startsWith('attr_')) {
      const attrName = fieldName.replace('attr_', '');
      const wasEnriched = pendingEnrichmentData.attributes && pendingEnrichmentData.attributes[attrName];
      const currentValue = getSessionValue(`attr_${attrName}`, attributeValues[attrName] || '');
      return wasEnriched && currentValue && currentValue.trim() !== '';
    }
    
    return false;
  };

  const handleCancelButtonClick = () => {
    const sessionKey = `${batch_id}_${rowId}`;
    const storedData = sessionStorage.getItem(sessionKey);

    if (storedData) {
      setConfirmModalOpen(true);
    } else {
      handleCancelClick();
    }
  };

  const handleConfirmNo = () => {
    const sessionKey = `${batch_id}_${rowId}`;
    sessionStorage.removeItem(sessionKey);

    setConfirmModalOpen(false);
    handleCancelClick();
  };

  const handleConfirmYes = () => {
    setConfirmModalOpen(false);
    handleCancelClick();
  };

  // FIX 2: Enhanced save button handler with guaranteed long description inclusion
  const handleSaveButtonClick = async () => {
    const sessionKey = `${batch_id}_${rowId}`;
    let changedFieldsData = {};

    try {
      const storedData = sessionStorage.getItem(sessionKey);
      if (storedData) {
        changedFieldsData = JSON.parse(storedData);
      }
    } catch (error) {
      console.error("Error getting session data for save:", error);
    }

    // FIX 2: ALWAYS ensure Long Description is in the payload with current value
    const currentLongDesc = generatedLongDesc || getSessionValue('Long Description', getValue('long_description', editData?.['Long Description'] || ''));
    if (currentLongDesc && currentLongDesc.trim() !== '') {
      changedFieldsData['Long Description'] = currentLongDesc;
      console.log('💾 Including Long Description in save payload:', currentLongDesc);
    }

    if (Object.keys(changedFieldsData).length === 0) {
      showErrorToast("No changes to save. Please update fields before saving.");
      return;
    }

    function showErrorToast(message) {
      const toast = document.createElement('div');
      toast.className = 'error-toast';

      toast.innerHTML = `
    <div style="display: flex; align-items: center; gap: 8px;">
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
      </svg>
      <span>${message}</span>
    </div>
  `;

      toast.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    padding: 16px 24px;
    borderRadius: 12px;
    box-shadow: 0 10px 25px rgba(239, 68, 68, 0.3);
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    font-weight: 500;
    max-width: 90%;
    min-width: 300px;
    animation: slideInFromTop 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border: 1px solid rgba(255, 255, 255, 0.2);
  `;

      document.body.appendChild(toast);

      setTimeout(() => {
        toast.style.animation = 'slideOutToTop 0.3s cubic-bezier(0.6, 0, 0.8, 0.2)';
        setTimeout(() => {
          if (toast.parentNode) {
            toast.remove();
          }
        }, 300);
      }, 4000);
    }

    let standardName = '';
    let standardName2 = '';

    if (changedFieldsData.hasOwnProperty('Standard Name')) {
      standardName = changedFieldsData['Standard Name'] || '';
    } else if (localEditData?.name) {
      standardName = localEditData.name;
    } else {
      standardName = editData?.['Standard Name'] || editData?.name || '';
    }

    if (changedFieldsData.hasOwnProperty('Standard Name 2')) {
      standardName2 = changedFieldsData['Standard Name 2'] || '';
    } else if (localEditData?.standard_name_2) {
      standardName2 = localEditData.standard_name_2;
    } else {
      standardName2 = editData?.['Standard Name 2'] || editData?.standard_name_2 || '';
    }

    const hasStandardName1 = standardName && standardName.trim() !== '';
    const hasStandardName2 = standardName2 && standardName2.trim() !== '';

    if (!hasStandardName1 && !hasStandardName2) {
      showErrorToast("At least one Standard Name (Standard Name or Standard Name 2) is required");
      return;
    }

    let comments = '';
    if (changedFieldsData.hasOwnProperty('Comments')) {
      comments = changedFieldsData['Comments'] || '';
    } else if (localEditData?.comments) {
      comments = localEditData.comments;
    } else {
      comments = editData?.['Comments'] || editData?.comments || '';
    }

    if (!comments || comments.trim() === '') {
      showErrorToast("Comments field is required");
      return;
    }

    let status = '';
    if (changedFieldsData.hasOwnProperty('Status')) {
      status = changedFieldsData['Status'] || '';
    } else {
      status = getValue('status', editData?.['Status'] || '');
    }

    if (!status || status === "Not Enriched") {
      showErrorToast("Status is required and cannot be 'Not Enriched'");
      return;
    }

    setModalOpen(false);

    const attributeData = {};
    Object.keys(changedFieldsData).forEach((key) => {
      if (key.startsWith("attr_")) {
        const attrName = key.replace("attr_", "");
        attributeData[attrName] = changedFieldsData[key];
        delete changedFieldsData[key];
      }
    });

    if (Object.keys(attributeData).length > 0) {
      changedFieldsData.attribute = attributeData;
    }

    if (referenceLinks.length > 0) {
      changedFieldsData.reference_links = referenceLinks.map(link => ({
        label: link.label,
        url: link.url
      }));
      console.log('📎 Adding reference links to save payload:', changedFieldsData.reference_links);
    }

    const payload = {
      batch_id,
      row_id: rowId,
      fields: changedFieldsData
    };

    console.log('💾 Final save payload with long description:', payload);

    try {
      const updateResponse = await updateData(payload);

      if (updateResponse && updateResponse.status === 'success') {
        const message = updateResponse.message;
        const batchIdMatch = message.match(/batch_id\s+([a-f0-9-]+)/i);
        let responseBatchId = null;

        if (batchIdMatch && batchIdMatch[1]) {
          responseBatchId = batchIdMatch[1];
        }

        sessionStorage.removeItem(sessionKey);
        setChangedFields(new Set());

        handleToastOpen("success", "Changes saved successfully");
        handleSaveClick(true, responseBatchId);

      } else {
        handleToastOpen("error", updateResponse?.message || "Failed to save changes");
        handleSaveClick(false);
      }

    } catch (error) {
      console.error("Error updating data:", error);
      handleToastOpen("error", "Failed to save changes");
      handleSaveClick(false);
    }
  };

  const ReferenceLinksSection = () => {
    if (referenceLinks.length === 0) return null;

    const isFromEnrichment = showApprovalButtons || enrichmentApproved;
    const sectionTitle = isFromEnrichment ? 
      "AI Generated Reference Links" : 
      "Saved Reference Links";
    const sectionDescription = isFromEnrichment ? 
      "These links contain the technical specifications used for enrichment:" :
      "Previously saved reference links for this material:";

    return (
      <CBox sx={{ 
        mt: 3, 
        p: 2, 
        backgroundColor: '#f8fafc', 
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <CBox sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <LinkIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
          <CText variant="subtitle2" sx={{ 
            fontWeight: 600, 
            fontSize: '0.9rem', 
            color: '#1e293b' 
          }}>
            {sectionTitle} ({referenceLinks.length})
          </CText>
        </CBox>
        <CText sx={{ 
          fontSize: '0.85rem', 
          color: '#64748b', 
          mb: 2,
          lineHeight: 1.5
        }}>
          {sectionDescription}
        </CText>
        
        {referenceLinks.map((link, index) => (
          <CBox key={index} sx={{ mb: 1.5 }}>
            <CBox 
              component="a"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#ffffff',
                padding: '12px 16px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                textDecoration: 'none',
                color: '#1e293b',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  backgroundColor: '#f1f5f9',
                  borderColor: '#3b82f6',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)'
                }
              }}
            >
              <CBox sx={{ flex: 1 }}>
                <CText sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 600, 
                  color: '#3b82f6',
                  mb: 0.5
                }}>
                  {link.label}
                </CText>
                <CText sx={{ 
                  fontSize: '0.8rem', 
                  color: '#64748b',
                  wordBreak: 'break-all',
                  lineHeight: 1.4
                }}>
                  {link.url}
                </CText>
              </CBox>
              <OpenInNewIcon sx={{ 
                fontSize: 16, 
                color: '#64748b',
                ml: 1,
                flexShrink: 0
              }} />
            </CBox>
          </CBox>
        ))}
      </CBox>
    );
  };

  const searchButtonStyle = {
    ml: 1,
    border: '1px solid #ddd',
    borderRadius: '4px',
    height: '39px',
    width: '39px',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      border: '1px solid #ccc'
    }
  };

  return (
    <>
      <CModal
        open={modalOpen}
        handleClose={handleCancelButtonClick}
        title="Edit Material Details"
        actions={[
          {
            label: "Cancel",
            onClick: handleCancelButtonClick,
            color: "error",
            variant: "outlined",
            startIcon: <CancelIcon />
          },
          {
            label: "Save Changes",
            onClick: handleSaveButtonClick,
            color: "primary",
            startIcon: <SaveIcon />
          }
        ]}
        maxWidth="lg"
        width="90%"
        headerBgColor="#002A5B"
      >
        <CBox sx={{
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginBottom: '15px'
        }}>
          <InfoIcon sx={{ color: '#1976d2', fontSize: 20 }} />
          <CText sx={{
            fontSize: '0.875rem',
            color: '#1565c0',
            fontWeight: 500
          }}>
            Tip: Fill in Material Descriptions (1,2,3), Manufacturers (1,2,3), and Part Numbers (1,2,3), then click "AI Enrich" to automatically populate technical specifications.
          </CText>
        </CBox>

        {isEnriching && (
          <CLoader
            progress={enrichmentProgress}
          />
        )}

        <CBox sx={{
          p: 2,
          maxHeight: 'calc(100vh - 180px)',
          backgroundColor: '#f9f9f9',
          opacity: isEnriching ? 0.7 : 1,
          pointerEvents: isEnriching ? 'none' : 'auto',
          transition: 'opacity 0.3s ease'
        }}>
          <CGrid container spacing={2}>
            {/* Left Column - Material Info */}
            <CGrid item xs={12} md={6}>
              <CBox sx={{ mb: 2, p: 1.5, backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <CText sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#002A5B', mb: 1.5 }}>Material Information</CText>
                <CGrid container spacing={1.5}>
                  <CGrid item xs={12} sm={3.2}>
                    <CTooltip title={getValue('material_number_index', editData?.['Material Number Index'] || '')} placement="top" arrow disableHoverListener={!getValue('material_number_index', editData?.['Material Number Index'] || '')}>
                      <CInput label="Material Number" value={getValue('material_number_index', editData?.['Material Number Index'] || '')} onChange={(e) => handleFieldChangeWrapper('Material Number', e.target.value)} fullWidth variant="outlined" size="small" disabled id="material_number" />
                    </CTooltip>
                  </CGrid>
                  <CGrid item xs={12} sm={8.8}>
                    <CBox display="flex" alignItems="center">
                      <CInput label="Material Description" value={getValue('material_description', editData?.['Material Description'] || '')} onChange={(e) => handleFieldChangeWrapper('Material Description', e.target.value)} fullWidth variant="outlined" size="small" disabled uppercase={true} id="material_description" />
                      <CButton type="icon" customIcon={<SearchIcon />} onClick={() => openMaterialSearch(localEditData?.material_description)} size="small" iconButton={true} sx={searchButtonStyle} disabled={!getSessionValue('Material Description', localEditData?.material_description || '').trim()} />
                    </CBox>
                  </CGrid>
                  <CGrid item xs={12}>
                    <CBox display="flex" alignItems="center">
                      <CInput label="Material Description 2" value={getSessionValue('Material Description 2', localEditData?.material_description_2 || '')} onChange={(e) => handleFieldChangeWrapper('Material Description 2', e.target.value)} fullWidth variant="outlined" size="small" disabled uppercase={true} id="material_description_2" />
                      <CButton type="icon" customIcon={<SearchIcon />} onClick={() => openMaterialSearch(getSessionValue('Material Description 2', localEditData?.material_description_2 || ''))} size="small" iconButton={true} sx={searchButtonStyle} disabled={!getSessionValue('Material Description 2', localEditData?.material_description_2 || '').trim()} />
                    </CBox>
                  </CGrid>
                  <CGrid item xs={12}>
                    <CBox display="flex" alignItems="center">
                      <CInput label="Material Description 3" value={getSessionValue('Material Description 3', localEditData?.material_description_3 || '')} onChange={(e) => handleFieldChangeWrapper('Material Description 3', e.target.value)} fullWidth variant="outlined" size="small" disabled uppercase={true} id="material_description_3" />
                      <CButton type="icon" customIcon={<SearchIcon />} onClick={() => openMaterialSearch(getSessionValue('Material Description 3', localEditData?.material_description_3 || ''))} size="small" iconButton={true} sx={searchButtonStyle} disabled={!getSessionValue('Material Description 3', localEditData?.material_description_3 || '').trim()} />
                    </CBox>
                  </CGrid>
                </CGrid>
              </CBox>
              <CBox sx={{ px: 1, pt: 1.5, backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: '140px' }}>
                <CText sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#002A5B', mb: 1.5 }}>Classification & Standards</CText>
                <CGrid container spacing={1.5} sx={{ m1: 1 }}>
                  <CGrid item xs={12} sm={6}><CInput label="Commodity" value={getSessionValue('Commodity', getValue('commodity', editData?.['Commodity'] || ''))} onChange={(event) => handleFieldChangeWrapper('Commodity', event.target.value)} size="small" fullWidth placeholder="Enter Commodity" sx={{ mb: 3 }} id="commodity" disabled /></CGrid>
                  <CGrid item xs={12} sm={6} marginTop={0.2}><CDropdown selectedValue={getSessionValue('Commodity 2', localEditData?.commodity_2 || '')} onValueChange={(val) => handleFieldChangeWrapper('Commodity 2', val || '')} options={(commodities || []).map(item => ({value: item.level_4, label: item.level_4}))} label="Commodity 2" size="small" showSearch={true} clearable={true} useSessionStorage={true} sessionKey={`${batch_id}_${rowId}`} sessionField="Commodity 2" /></CGrid>
                  <CGrid item xs={12} sm={6} marginTop={-3}><CDropdown selectedValue={getSessionValue('Standard Name', localEditData?.standard_name || '')} onValueChange={(val) => handleFieldChangeWrapper('Standard Name', val || '')} options={(standards || []).map(std => ({value: std.name, label: std.name}))} label="Standard Name" size="small" required={true} showSearch={true} clearable={true} useSessionStorage={true} sessionKey={`${batch_id}_${rowId}`} sessionField="Standard Name" disabled={isFieldDisabled('Standard Name')} /></CGrid>
                  <CGrid item xs={12} sm={6} marginTop={-3}><CInput label="Standard Name 2" value={getSessionValue('Standard Name 2', localEditData?.standard_name_2 || '')} onChange={(event) => handleFieldChangeWrapper('Standard Name 2', event.target.value)} size="small" fullWidth placeholder="Enter standard name" sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '6px', '&.Mui-disabled': { backgroundColor: '#f8fafc', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }, '& .MuiInputBase-input': { color: '#64748b', fontWeight: 500 }}}}} id="standard_name_2" required={true} disabled={isFieldDisabled('Standard Name 2')} /></CGrid>
                </CGrid>
              </CBox>
            </CGrid>
            {/* Right Column - Manufacturers */}
            <CGrid item xs={12} md={6}>
              <CBox sx={{ mb: 2, p: 1.5, backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <CText sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#002A5B', mb: 1.5 }}>Manufacturer Information</CText>
                <CGrid container spacing={1.5}>
                  <CGrid item xs={12}><CBox display="flex" alignItems="center"><CBox flexGrow={1} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1.5}><CInput label="Manufacturer 1" value={getSessionValue('Manufacturer 1', localEditData?.manufacturer_name_1 || '')} onChange={(e) => handleFieldChangeWrapper('Manufacturer 1', e.target.value)} fullWidth variant="outlined" size="small" uppercase={true} id="manufacturer_name_1" /><CInput label="Part Number 1" value={getSessionValue('Part Number 1', localEditData?.manufacturer_part_number_1 || '')} onChange={(e) => handleFieldChangeWrapper('Part Number 1', e.target.value)} fullWidth variant="outlined" size="small" uppercase={true} id="manufacturer_part_number_1" /></CBox><CButton type="icon" customIcon={<SearchIcon />} onClick={() => openManufacturerSearch(getSessionValue('Manufacturer 1', localEditData?.manufacturer_name_1 || ''), getSessionValue('Part Number 1', localEditData?.manufacturer_part_number_1 || ''))} size="small" iconButton={true} sx={searchButtonStyle} disabled={!getSessionValue('Manufacturer 1', localEditData?.manufacturer_name_1 || '').trim() && !getSessionValue('Part Number 1', localEditData?.manufacturer_part_number_1 || '').trim()} /></CBox></CGrid>
                  <CGrid item xs={12}><CBox display="flex" alignItems="center"><CBox flexGrow={1} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1.5}><CInput label="Manufacturer 2" value={getSessionValue('Manufacturer 2', localEditData?.manufacturer_name_2 || '')} onChange={(e) => handleFieldChangeWrapper('Manufacturer 2', e.target.value)} fullWidth variant="outlined" size="small" uppercase={true} id="manufacturer_name_2" /><CInput label="Part Number 2" value={getSessionValue('Part Number 2', localEditData?.manufacturer_part_number_2 || '')} onChange={(e) => handleFieldChangeWrapper('Part Number 2', e.target.value)} fullWidth variant="outlined" size="small" uppercase={true} id="manufacturer_part_number_2" /></CBox><CButton type="icon" customIcon={<SearchIcon />} onClick={() => openManufacturerSearch(getSessionValue('Manufacturer 2', localEditData?.manufacturer_name_2 || ''), getSessionValue('Part Number 2', localEditData?.manufacturer_part_number_2 || ''))} size="small" iconButton={true} sx={searchButtonStyle} disabled={!getSessionValue('Manufacturer 2', localEditData?.manufacturer_name_2 || '').trim() && !getSessionValue('Part Number 2', localEditData?.manufacturer_part_number_2 || '').trim()} /></CBox></CGrid>
                  <CGrid item xs={12}><CBox display="flex" alignItems="center"><CBox flexGrow={1} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1.5}><CInput label="Manufacturer 3" value={getSessionValue('Manufacturer 3', localEditData?.manufacturer_name_3 || '')} onChange={(e) => handleFieldChangeWrapper('Manufacturer 3', e.target.value)} fullWidth variant="outlined" size="small" uppercase={true} id="manufacturer_name_3" /><CInput label="Part Number 3" value={getSessionValue('Part Number 3', localEditData?.manufacturer_part_number_3 || '')} onChange={(e) => handleFieldChangeWrapper('Part Number 3', e.target.value)} fullWidth variant="outlined" size="small" uppercase={true} id="manufacturer_part_number_3" /></CBox><CButton type="icon" customIcon={<SearchIcon />} onClick={() => openManufacturerSearch(getSessionValue('Manufacturer 3', localEditData?.manufacturer_name_3 || ''), getSessionValue('Part Number 3', localEditData?.manufacturer_part_number_3 || ''))} size="small" iconButton={true} sx={searchButtonStyle} disabled={!getSessionValue('Manufacturer 3', localEditData?.manufacturer_name_3 || '').trim() && !getSessionValue('Part Number 3', localEditData?.manufacturer_part_number_3 || '').trim()} /></CBox></CGrid>
                </CGrid>
              </CBox>
              <CBox sx={{ mb: 2, px: 1.5, pt: 1.5, backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: '140px' }}>
                <CText sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#002A5B', mb: 1.5 }}>Status & Additional Info</CText>
                <CGrid container spacing={1.5} sx={{ mb: 0 }}>
                  <CGrid item xs={12} sm={12}><CInput label="QC Comments" value={getSessionValue('QC Comments', localEditData?.qc_comments || '')} onChange={(e) => handleFieldChangeWrapper('QC Comments', e.target.value)} fullWidth variant="outlined" size="small" id="qc_comments" disabled /></CGrid>
                  <CGrid item xs={12} sm={6}><CDropdown selectedValue={getSessionValue('Comments', localEditData?.comments || '')} onValueChange={(val) => handleCommentChangeWrapper(val || '')} options={[{value: 'MPN info. Missing', label: 'MPN info. Missing'}, {value: 'Attributes info missing', label: 'Attributes info missing'}, {value: 'MPN and Attribute info missing', label: 'MPN and Attribute info missing'}, {value: 'Enriched', label: 'Enriched'}]} label="Comments" size="small" fullWidth showSearch={true} clearable={true} required={true} useSessionStorage={true} sessionKey={`${batch_id}_${rowId}`} sessionField="Comments" /></CGrid>
                  <CGrid item xs={12} sm={6}><CDropdown selectedValue={getSessionValue('Status', getValue('status', editData?.['Status'] || 'Not Enriched'))} onValueChange={(val) => handleFieldChangeWrapper('Status', val || '')} options={[{value: 'Not Enriched', label: 'Not Enriched'}, {value: 'To be Reviewed', label: 'To be Reviewed'}, {value: 'Processed', label: 'Processed'}]} label="Status" size="small" fullWidth required={true} useSessionStorage={true} sessionKey={`${batch_id}_${rowId}`} sessionField="Status" /></CGrid>
                </CGrid>
              </CBox>
            </CGrid>
            {/* Attributes Section */}
            {attributeFieldNames.length > 0 && (
              <CGrid item xs={12} marginTop={-2}>
                <CBox sx={{ mb: 2, p: 2, backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                  <CBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid #f5f5f5' }}>
                    <CText sx={{ fontSize: '1rem', fontWeight: 600, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>Material Attributes</CText>
                    {!showApprovalButtons && (<CButton type="custom" variant="contained" size="small" onClick={handleMaterialEnrichment} disabled={isEnriching || !isCommentsEmpty()} startIcon={isEnriching ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />} label={isEnriching ? "Enriching..." : "AI Enrich"} sx={{ minWidth: '130px', fontSize: '0.8rem', fontWeight: 500, padding: '8px 16px', borderRadius: '6px', backgroundColor: '#2563eb', color: 'white', textTransform: 'none', boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)', '&:hover': { backgroundColor: '#1d4ed8', boxShadow: '0 4px 8px rgba(37, 99, 235, 0.3)' }, '&:disabled': { backgroundColor: '#94a3b8', color: 'white' }}} />)}
                    {showApprovalButtons && (<CBox sx={{ display: 'flex', gap: 1.5 }}><CButton type="custom" variant="contained" size="small" onClick={handleApproveEnrichment} startIcon={<ThumbUpIcon sx={{ fontSize: 16 }} />} label="Approve" sx={{ minWidth: '110px', fontSize: '0.8rem', fontWeight: 500, padding: '8px 16px', borderRadius: '6px', backgroundColor: '#10b981', color: 'white', textTransform: 'none', boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)', '&:hover': { backgroundColor: '#059669', boxShadow: '0 4px 8px rgba(16, 185, 129, 0.3)' }}} /><CButton type="custom" variant="outlined" size="small" onClick={handleRejectEnrichment} startIcon={<ThumbDownIcon sx={{ fontSize: 16 }} />} label="Reject" sx={{ minWidth: '110px', fontSize: '0.8rem', fontWeight: 500, padding: '8px 16px', borderRadius: '6px', borderColor: '#ef4444', color: '#ef4444', textTransform: 'none', '&:hover': { backgroundColor: '#fef2f2', borderColor: '#dc2626', color: '#dc2626' }}} /></CBox>)}
                  </CBox>
                  {enrichmentStatus && (<CBox sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, mb: 2, borderRadius: '8px', backgroundColor: enrichmentStatus === 'success' ? '#f0fdf4' : enrichmentStatus === 'warning' ? '#fffbeb' : '#fef2f2', border: `1px solid ${enrichmentStatus === 'success' ? '#bbf7d0' : enrichmentStatus === 'warning' ? '#fed7aa' : '#fecaca'}` }}>{getEnrichmentStatusIcon()}<CBox sx={{ flex: 1 }}><CText variant="body2" sx={{ color: enrichmentStatus === 'success' ? '#15803d' : enrichmentStatus === 'warning' ? '#d97706' : '#dc2626', fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.5, mb: 0.5 }}>{enrichmentMessage}</CText>{enrichmentStatus === 'warning' && !enrichmentAttempted && (<CText sx={{ fontSize: '0.8rem', color: '#92400e', fontStyle: 'italic', lineHeight: 1.4 }}>Make sure all required fields are filled correctly before trying again.</CText>)}{enrichmentStatus === 'error' && (<CText sx={{ fontSize: '0.8rem', color: '#991b1b', fontStyle: 'italic', lineHeight: 1.4 }}>Please check your internet connection and try again in a few moments.</CText>)}</CBox></CBox>)}
                  <CGrid container spacing={2}>{attributeFieldNames.map((fieldName) => (<CGrid item xs={12} sm={6} md={3} key={fieldName}><CInput label={fieldName} value={getSessionValue(`attr_${fieldName}`, attributeValues[fieldName] || '')} onChange={(e) => handleAttributeChangeWrapper(fieldName, e.target.value)} fullWidth variant="outlined" size="small" id={`attr_${fieldName}`} className="longdesc" disabled={isFieldDisabled(`attr_${fieldName}`)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', '&.Mui-disabled': { backgroundColor: '#f8fafc', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }, '& .MuiInputBase-input': { color: '#64748b', fontWeight: 500 }}}}} /></CGrid>))}</CGrid>
                  <ReferenceLinksSection />
                  {citationUrl && referenceLinks.length === 0 && (<CBox sx={{ mt: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><CBox sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}><SearchIcon sx={{ color: '#3b82f6', fontSize: 20 }} /><CText variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>Verification Search Query</CText></CBox><CText sx={{ fontSize: '0.85rem', color: '#64748b', mb: 1.5, lineHeight: 1.5 }}>Use this search query to verify the technical specifications:</CText><CBox sx={{ backgroundColor: '#ffffff', padding: '12px 16px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#1e293b', fontFamily: 'monospace', lineHeight: 1.5, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { backgroundColor: '#f1f5f9', borderColor: '#cbd5e1' }}} onClick={() => navigator.clipboard.writeText(citationUrl).then(() => { console.log('Search query copied to clipboard'); })}>{citationUrl}</CBox><CText sx={{ fontSize: '0.75rem', color: '#64748b', mt: 1, fontStyle: 'italic' }}>Click to copy search query to clipboard</CText></CBox>)}
                </CBox>
              </CGrid>
            )}
            {/* Long Description */}
            <CGrid item xs={12} marginTop={-2}>
              <CBox sx={{ p: 1.5, backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <CText sx={{ fontSize: '0.9rem', fontWeight: 500, color: '#002A5B', mb: 1 }}>Long Description & Final Comments</CText>
                <CGrid container spacing={1.5}>
                  <CGrid item xs={12}><CInput label="Enriched Long Description" value={generatedLongDesc || getSessionValue('Long Description', getValue('long_description', editData?.['Long Description'] || '—'))} onChange={(e) => handleFieldChangeWrapper('Long Description', e.target.value)} fullWidth variant="outlined" size="small" uppercase={true} id="long_description" disabled /></CGrid>
                  <CGrid item xs={12}><CInput label="General Comments" value={getSessionValue('General Comments', localEditData?.general_comments || '')} onChange={(e) => handleFieldChangeWrapper('General Comments', e.target.value)} fullWidth variant="outlined" size="small" multiline={true} rows={2} id="general_comments" /></CGrid>
                </CGrid>
              </CBox>
            </CGrid>
          </CGrid>
        </CBox>
      </CModal>
      <ConfirmCancelModal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} onConfirm={handleConfirmYes} onCancel={handleConfirmNo} />
    </>
  );
};

export default EditableModal;